import { deriveRng, nextRange } from './rng';
import { isPassable } from '../content/terrain';
import { rotateQuarterCW, startRotation } from '../content/maps';
import type { ParsedMap, StartSlot } from '../content/maps';

/*
 * נקודות הפתיחה — **מוגרלות בכל משחק, ולא כתובות במפה.**
 *
 * בר: "הבסיסים צריכים להשתבץ בצורה רנדומלית (אבל הגיונית, לא קרובות מדי), כדי
 * שלא כל משחק ייראה אותו הדבר."
 *
 * **העיקרון שמאפשר את זה בלי לשבור הוגנות:** המפה עצמה סימטרית — ב-180 מעלות
 * תמיד, וב-90 במפות של ארבעה (נאכף ב-`content/maps/maps.test.ts`). לכן די
 * להגריל **נקודה אחת** ולגזור את השאר בסיבוב: אם העוגן חוקי, כל הסיבובים שלו
 * חוקיים **בהכרח**, כי הקרקע והעפרות מתחתיהם זהות אריח-באריח. הוגנות אינה
 * נבדקת כאן — היא נובעת מהבנייה.
 *
 * **הנקודה נדגמת מכל המפה, ומה שמחזיק אותה "הגיונית" הוא רשימת הפסילות ולמטה
 * ולא הקרבה לנקודה הכתובה.**
 *
 * הגרסה הראשונה טלטלה סביב הנקודה הכתובה ברדיוס של שמינית מצלע המפה, מתוך
 * מחשבה שמחבר המפה בחר פינה עם עפרות, מרחק ומסלול, ושטלטול משמר את הכוונה.
 * **בר בדק ואמר "אני תמיד מתחיל באותו מקום" — והמדידה אישרה אותו:** הבסיס זז
 * לכל היותר 14-16% מצלע המפה ו**נשאר תמיד באותה פינה**. מכיוון שהמצלמה נפתחת
 * על הבסיס, הפתיחה נראתה זהה לחלוטין. פיצ'ר שעובד ולא נראה הוא פיצ'ר שלא קיים.
 *
 * הכוונה של מחבר המפה לא אבדה — היא פשוט **נכתבה כתנאים** במקום כמרחק: עפרות
 * בטווח, שטח פנוי לבנות בו, מרחק הליכה שאינו קצר מזה של המפה הכתובה, ופער
 * מינימלי. נקודה שעוברת את כולם היא נקודה טובה, גם אם היא בפינה השנייה של
 * המפה. **נמדד: 187-197 פריסות שונות מתוך 200 בכל תשע המפות, ואפס עד נפילה
 * אחת חזרה לכתוב.**
 *
 * הסדר של המשבצות המוחזרות הוא **סדר הסיבובים של המפה הכתובה** — ‏[0,2,1,3] —
 * ולכן `startRotation` ממשיכה להחזיר את אותם רבעי סיבוב לכל שחקן, וכל מה
 * שנשען עליה (פריסת הבסיס, ספירלת הבנייה, טסט ההוגנות) ממשיך לעבוד כמות שהוא.
 */

/**
 * כמה ניסיונות לפני שנופלים חזרה לנקודות הכתובות.
 *
 * גבוה בכוונה: הדגימה חופשית על כל המפה, ולכן רוב הנקודות נפסלות (מים, מרכז
 * המפה, רחוק מעפרות). זה עולה ~9.5ms פעם אחת בפתיחת קרב על המפה הגדולה ביותר —
 * ‏152² עם ארבעה שחקנים — וזה המחיר של פתיחה שנראית אחרת בכל משחק.
 */
const ATTEMPTS = 400;

/**
 * רדיוס השטח שחייב להיות פנוי סביב העוגן.
 *
 * מבני הפתיחה מגיעים עד היסט 5 מהנקודה (מפעל עיבוד ב-`dx:3` ברוחב 3), והם
 * מסתובבים — כלומר הם יכולים לנחות בכל אחד מארבעת הכיוונים. ‏6 מכסה את כולם
 * ומשאיר שוליים לכוח הפתיחה.
 */
const CLEAR_RADIUS = 6;

/** מרחק מרבי לעפרות, באריחי מנהטן. אותו סף בדיוק שנאכף על המפות הכתובות. */
const ORE_RANGE = 30;

/** מרחק מינימלי בין שני בסיסים, באריחי צ'בישב. גם זה הסף של המפות הכתובות. */
const MIN_GAP = 25;

/**
 * כמה מותר שמסלול ההליכה אל השכן יתקצר ביחס למפה הכתובה, באחוזים.
 *
 * **מרחק אווירי משקר** (מלכודת 45): נקודה שנראית רחוקה יכולה לשבת מעבר לגשר
 * ולהיות קרובה בהליכה, ומפה בלי זמן בנייה אינה מפה מהירה אלא זירה. לכן הבדיקה
 * היא על מסלול אמיתי, ולא על ההפרש בקואורדינטות.
 */
const MIN_TRAVEL_PCT = 85;

/**
 * מסובב נקודה ברבעי סיבוב סביב מרכז המפה, עם כיוון השעון.
 * אותה פונקציה שמסובבת את הקרקע — ולכן התוצאה נוחתת על אותו סוג אריח.
 */
function rotateSlot(slot: StartSlot, turns: number, size: number): StartSlot {
    let point = slot;
    for (let i = 0; i < ((turns % 4) + 4) % 4; i++) {
        point = rotateQuarterCW(point.x, point.y, size);
    }
    return { x: point.x, y: point.y };
}

/** האם כל השטח סביב הנקודה עביר — מקום לבסיס ולכוח הפתיחה. */
function clearAround(map: ParsedMap, slot: StartSlot): boolean {
    for (let dy = -CLEAR_RADIUS; dy <= CLEAR_RADIUS; dy++) {
        for (let dx = -CLEAR_RADIUS; dx <= CLEAR_RADIUS; dx++) {
            const x = slot.x + dx;
            const y = slot.y + dy;
            if (x < 0 || y < 0 || x >= map.w || y >= map.h) return false;
            if (!isPassable(map.terrain[y * map.w + x] ?? 0)) return false;
        }
    }
    return true;
}

/** המרחק לעפרות הקרובות ביותר, באריחי מנהטן. `-1` אם אין עפרות בכלל. */
function oreDistance(map: ParsedMap, slot: StartSlot): number {
    let best = -1;
    for (let i = 0; i < map.ore.length; i++) {
        if ((map.ore[i] ?? 0) === 0) continue;
        const x = i % map.w;
        const y = (i - x) / map.w;
        const d = Math.abs(x - slot.x) + Math.abs(y - slot.y);
        if (best < 0 || d < best) best = d;
    }
    return best;
}

/**
 * מרחק הליכה בצעדים מכל אריח לאריח המוצא, או `-1` למנותק.
 *
 * ‏BFS ולא Dijkstra: כאן שואלים **טופולוגיה** ("האם הבסיס האחר מעבר לנהר"),
 * לא זמן נסיעה. צעד אחיד מספיק לזה, ועולה מעבר אחד על המפה.
 */
function stepDistances(map: ParsedMap, from: StartSlot): Int32Array {
    const size = map.w * map.h;
    const dist = new Int32Array(size).fill(-1);
    const start = from.y * map.w + from.x;
    if (start < 0 || start >= size || !isPassable(map.terrain[start] ?? 0)) return dist;

    const queue = new Int32Array(size);
    let head = 0;
    let tail = 0;
    dist[start] = 0;
    queue[tail++] = start;

    while (head < tail) {
        const tile = queue[head++] ?? 0;
        const x = tile % map.w;
        const y = (tile - x) / map.w;
        const step = (dist[tile] ?? 0) + 1;
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                if (dx === 0 && dy === 0) continue;
                const nx = x + dx;
                const ny = y + dy;
                if (nx < 0 || ny < 0 || nx >= map.w || ny >= map.h) continue;
                const next = ny * map.w + nx;
                if ((dist[next] ?? -1) >= 0) continue;
                if (!isPassable(map.terrain[next] ?? 0)) continue;
                // בלי חיתוך פינות, בדיוק כמו ב-`sim/path.ts`.
                if (dx !== 0 && dy !== 0) {
                    if (!isPassable(map.terrain[y * map.w + nx] ?? 0)) continue;
                    if (!isPassable(map.terrain[ny * map.w + x] ?? 0)) continue;
                }
                dist[next] = step;
                queue[tail++] = next;
            }
        }
    }
    return dist;
}

/** המרחק בצעדים אל המשבצת הקרובה ביותר מבין השאר, או `-1` אם אחת מהן מנותקת. */
function nearestTravel(map: ParsedMap, slots: readonly StartSlot[]): number {
    const dist = stepDistances(map, slots[0] as StartSlot);
    let best = -1;
    for (let i = 1; i < slots.length; i++) {
        const slot = slots[i];
        if (!slot) continue;
        const d = dist[slot.y * map.w + slot.x] ?? -1;
        if (d < 0) return -1;
        if (best < 0 || d < best) best = d;
    }
    return best;
}

/** צ'בישב — אותו מדד שנאכף על המפות הכתובות. */
function gap(a: StartSlot, b: StartSlot): number {
    return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/**
 * **מלכודת 44, בגרסה שנוצרת בזמן ריצה.**
 *
 * ‏`ai/brain.ts` עדיין מחליט את כיוון ספירלת הבנייה לפי `x + y > (w + h) / 2`
 * בלבד. שתי נקודות על האלכסון הנגדי מקבלות את אותו סכום, ואז שני הבסיסים
 * מתרחבים לאותו כיוון במקום להיות תמונת ראי. במפה כתובה זה נתפס בטסט; בהגרלה
 * צריך לפסול את הרצועה מראש.
 */
function splitsOnDiagonal(map: ParsedMap, slots: readonly StartSlot[]): boolean {
    const half = (map.w + map.h) / 2;
    const a = slots[0];
    const b = slots[1];
    if (!a || !b) return false;
    return a.x + a.y > half !== (b.x + b.y > half);
}

/**
 * נקודות הפתיחה למשחק אחד. דטרמיניסטי לחלוטין ב-seed ובמפה, ולכן אותו קוד
 * ב-`המשך משחק` ובכל מחשב שני מייצר בדיוק את אותן נקודות.
 */
export function chooseStartSlots(map: ParsedMap, seed: number): StartSlot[] {
    const authored = map.def.startSlots;
    const anchor = authored[0];
    if (!anchor || authored.length < 2 || map.w !== map.h) return [...authored];

    const turns = authored.map((_slot, index) => startRotation(map.def, index));
    // משבצת שאינה על מסלול הסיבוב אינה ניתנת לגזירה — נשארים בכתוב.
    if (turns.some((value) => value < 0)) return [...authored];

    const baseline = nearestTravel(map, authored);
    if (baseline <= 0) return [...authored];

    /*
     * זרם נגזר וחסר-מצב, עם `player = -2` שאיש אינו משתמש בו: ההגרלה כאן אסור
     * לה לקדם את זרם העולם, אחרת מספר הניסיונות היה משנה את כל המשחק שאחריו.
     */
    const rng = deriveRng(seed, -2, -2);

    for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
        const candidate: StartSlot = {
            x: nextRange(rng, 0, map.w - 1),
            y: nextRange(rng, 0, map.h - 1),
        };
        if (!clearAround(map, candidate)) continue;

        const slots = turns.map((quarter) => rotateSlot(candidate, quarter, map.w));
        if (!splitsOnDiagonal(map, slots)) continue;

        let tooClose = false;
        for (let i = 0; i < slots.length && !tooClose; i++) {
            for (let j = i + 1; j < slots.length; j++) {
                if (gap(slots[i] as StartSlot, slots[j] as StartSlot) < MIN_GAP) {
                    tooClose = true;
                    break;
                }
            }
        }
        if (tooClose) continue;

        /*
         * הסף הוא **חד ממש** משני הצדדים, ובכוונה: הטסטים של המפות הכתובות
         * דורשים `< 30` ו-`>= 85%`, ועיגול מקל כאן היה מייצר הגרלה שעוברת
         * בייצור ונופלת בבדיקה — כלומר באג שמופיע רק בחלק מה-seeds.
         */
        const ore = oreDistance(map, candidate);
        if (ore < 0 || ore >= ORE_RANGE) continue;

        const walk = nearestTravel(map, slots);
        if (walk <= 0 || walk * 100 < baseline * MIN_TRAVEL_PCT) continue;

        return slots;
    }

    /*
     * **נפילה חזרה לכתוב אינה כישלון שקט** — היא ההתנהגות הישנה בדיוק, ומפה
     * צפופה שאין בה מקום לטלטול תשחק כמו שתמיד שיחקה.
     */
    return [...authored];
}
